import * as AccordionPrimitive from "@rn-primitives/accordion";
import { ChevronDown } from "lucide-react-native";
import { Platform, Pressable, Text, View } from "react-native";
import Animated, {
  FadeOutUp,
  LayoutAnimationConfig,
  LinearTransition,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from "react-native-reanimated";
import { twMerge } from "tailwind-merge";

export function Accordion({
  children,
  ...props
}: Omit<AccordionPrimitive.RootProps, "asChild"> &
  React.RefAttributes<AccordionPrimitive.RootRef>) {
  return (
    <LayoutAnimationConfig skipEntering>
      <AccordionPrimitive.Root
        asChild={Platform.OS !== "web"}
        {...(props as AccordionPrimitive.RootProps)}
      >
        <Animated.View layout={LinearTransition.duration(200)}>
          {children}
        </Animated.View>
      </AccordionPrimitive.Root>
    </LayoutAnimationConfig>
  );
}

export function AccordionItem({
  value,
  children,
  className,
  ...props
}: AccordionPrimitive.ItemProps &
  React.RefAttributes<AccordionPrimitive.ItemRef>) {
  return (
    <AccordionPrimitive.Item
      value={value}
      asChild
      className={twMerge(
        "border-border border-b",
        Platform.select({ web: "last:border-b-0" }),
        className,
      )}
      {...props}
    >
      <Animated.View
        layout={Platform.select({ native: LinearTransition.duration(200) })}
        className="native:overflow-hidden"
      >
        {children}
      </Animated.View>
    </AccordionPrimitive.Item>
  );
}

const Trigger = Platform.OS === "web" ? View : Pressable;

export function AccordionTrigger({
  children,
  className,
  ...props
}: AccordionPrimitive.TriggerProps & {
  children?: React.ReactNode;
} & React.RefAttributes<AccordionPrimitive.TriggerRef>) {
  const { isExpanded } = AccordionPrimitive.useItemContext();

  const progress = useDerivedValue(
    () =>
      isExpanded
        ? withTiming(1, { duration: 250 })
        : withTiming(0, { duration: 200 }),
    [isExpanded],
  );
  const chevronStyle = useAnimatedStyle(
    () => ({
      transform: [{ rotate: `${progress.value * 180}deg` }],
    }),
    [progress],
  );

  return (
    <AccordionPrimitive.Header>
      <AccordionPrimitive.Trigger {...props} asChild>
        <Trigger
          className={twMerge(
            "flex-row items-start justify-between gap-4 rounded-md font-semibold disabled:opacity-50",
            Platform.select({
              web: "flex flex-1 outline-none transition-all hover:underline focus-visible:underline disabled:pointer-events-none [&[data-state=open]>svg]:rotate-180",
            }),
            isExpanded && "pb-4",
            className,
          )}
        >
          {typeof children === "string" ? (
            <Text className="font-tobias text-lg font-medium leading-6">
              {children}
            </Text>
          ) : (
            <>{children}</>
          )}
          <Animated.View style={chevronStyle}>
            <ChevronDown
              size={16}
              color="#475569"
              className={twMerge(
                "shrink-0",
                Platform.select({
                  web: "pointer-events-none translate-y-0.5 transition-transform duration-200",
                }),
              )}
            />
          </Animated.View>
        </Trigger>
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

export function AccordionContent({
  children,
  className,
  ...props
}: AccordionPrimitive.ContentProps &
  React.RefAttributes<AccordionPrimitive.ContentRef>) {
  const { isExpanded } = AccordionPrimitive.useItemContext();

  return (
    <AccordionPrimitive.Content
      className={twMerge(
        "overflow-hidden",
        Platform.select({
          web: isExpanded ? "animate-accordion-down" : "animate-accordion-up",
        }),
      )}
      {...props}
    >
      <Animated.View
        exiting={Platform.select({ native: FadeOutUp.duration(200) })}
        className={twMerge("pb-4", className)}
      >
        {children}
      </Animated.View>
    </AccordionPrimitive.Content>
  );
}
